// @ts-strict-ignore
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ActionList, Box, Button, Icon } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { useProfileSetting } from 'hooks/useProfileSetting'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { isProduction } from 'utils/environment'
import track from 'utils/tracker'

export const ManageAccount = () => {
  const { t } = useTranslation()
  const {
    enableBilling,
    enableHppSettingsPage,
    enableLegalAgreements,
    enableManageMerchants,
    enablePaymentLimitsManagement,
    enableUsLaunchRestrictions,
  } = useFeatureFlags()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_ACCOUNT, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const { profile } = useProfileContext()
  const { isVerifiedBusinessAccount, isDeveloperAccount } = useAccountsContext()

  const permissions = useMappedRoles()

  const navigate = useNavigate()

  const isAccountViewOnly = !!profile?.viewOnly

  const canDisable =
    !isProduction && isDeveloperAccount ? false : isAccountViewOnly || !isVerifiedBusinessAccount

  const canViewLegalAgreements = permissions?.manageLegalAgreements?.canView ?? false
  const canViewManageChannels = permissions?.channels?.canView
  const canViewManageMerchants = permissions?.manageMerchants?.canView ?? false
  const canViewTeamMembers = permissions?.manageTeamMembers?.canView
  const canViewPaymentSupport = permissions?.paymentSupport?.canView

  const hasLegalAgreementProfileSetting = useProfileSetting('isLegalAgreementSectionEnabled')

  const handleItemClick = (e: { stopPropagation: () => void }, path: string) => {
    e?.stopPropagation()
    navigate(path)
  }

  const settingsItems = [
    ...(enableManageMerchants
      ? [
          {
            key: 'manage-merchants',
            id: 'manage-merchants',
            transparent: true,
            title: t('settingsManageMerchants'),
            testId: `manage-merchants`,
            disabled: canDisable && !canViewManageMerchants,
            BodyPre: t('settingsManageMerchantsDescription'),
            Avatar: (
              <Box paddingL={8} paddingR={4}>
                <Icon name="Storefront" size="large" />
              </Box>
            ),
            onItemClick: e =>
              handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_MERCHANT_DETAILS}`),
            CustomAction: (
              <Button
                onClick={e =>
                  handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_MERCHANT_DETAILS}`)
                }
                iconElement={<Icon name="CaretRight" />}
                variant="ghost"
              />
            ),
          },
        ]
      : []),
    ...(!enableUsLaunchRestrictions
      ? [
          {
            key: 'manage-channels',
            id: 'manage-channels',
            transparent: true,
            title: t('settingsManageChannels'),
            BodyPre: t('settingsManageChannelsDescription'),
            testId: 'manage-channels',
            disabled: !canViewManageChannels,
            Avatar: (
              <Box paddingL={8} paddingR={4}>
                <Icon name="QrCode" size="large" />
              </Box>
            ),
            onItemClick: e =>
              handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_CHANNELS}`),
            CustomAction: (
              <Button
                onClick={e =>
                  handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_CHANNELS}`)
                }
                iconElement={<Icon name="CaretRight" />}
                variant="ghost"
              />
            ),
          },
        ]
      : []),
  ]

  if (canViewPaymentSupport) {
    settingsItems.push({
      key: 'payment-support',
      id: 'payment-support',
      transparent: true,
      title: t('settingsPaymentSupport'),
      BodyPre: t('settingsPaymentSupportDescription'),
      testId: 'payment-support',
      disabled: canDisable,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="Question" size="large" />
        </Box>
      ),
      onItemClick: e =>
        handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_PAYMENT_SUPPORT}`),
      CustomAction: (
        <Button
          onClick={e =>
            handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_PAYMENT_SUPPORT}`)
          }
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  const canViewPaymentLimits = permissions?.paymentLimits?.canView ?? false

  if (enablePaymentLimitsManagement && canViewPaymentLimits) {
    settingsItems.push({
      key: 'payment-limits',
      id: 'payment-limits',
      transparent: true,
      title: t('settingsPaymentLimits'),
      BodyPre: t('settingsPaymentLimitsDescription'),
      testId: 'payment-limits',
      disabled: isAccountViewOnly,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="SlidersHorizontal" size="large" />
        </Box>
      ),
      onItemClick: e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.PAYMENT_LIMITS}`),
      CustomAction: (
        <Button
          onClick={e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.PAYMENT_LIMITS}`)}
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  if (enableHppSettingsPage) {
    settingsItems.push({
      key: 'hpp-settings',
      id: 'hpp-settings',
      transparent: true,
      title: t('hppSettings.title'),
      BodyPre: t('hppSettings.manageDescription'),
      testId: 'hpp-settings',
      disabled: canDisable,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="CreditCard" size="large" />
        </Box>
      ),
      onItemClick: e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.HPP_SETTINGS}`),
      CustomAction: (
        <Button
          onClick={e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.HPP_SETTINGS}`)}
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  if (enableBilling) {
    settingsItems.push({
      key: 'billing',
      id: 'billing',
      transparent: true,
      title: t('billing.title'),
      BodyPre: t('billing.manageDescription'),
      testId: 'billing',
      disabled: canDisable,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="CreditCard" size="large" />
        </Box>
      ),
      onItemClick: e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.BILLING}`),
      CustomAction: (
        <Button
          onClick={e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.BILLING}`)}
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  if (canViewTeamMembers) {
    settingsItems.push({
      key: 'team-members',
      id: 'team-members',
      transparent: true,
      title: t('settingsTeamMembers'),
      BodyPre: t('settingsTeamMembersDescription'),
      testId: 'team-members',
      disabled: false,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="UsersFour" size="large" />
        </Box>
      ),
      onItemClick: e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_TEAM_MEMBERS}`),
      CustomAction: (
        <Button
          onClick={e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_TEAM_MEMBERS}`)}
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  if (enableLegalAgreements || hasLegalAgreementProfileSetting) {
    settingsItems.unshift({
      key: 'legal-agreements',
      id: 'legal-agreements',
      transparent: true,
      title: t('legalAgreements.title'),
      BodyPre: t('legalAgreements.manageDescription'),
      testId: 'legal-agreements',
      disabled: !canViewLegalAgreements,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="BuildingOffice" size="large" />
        </Box>
      ),
      onItemClick: e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.LEGAL_AGREEMENTS}`),
      CustomAction: (
        <Button
          onClick={e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.LEGAL_AGREEMENTS}`)}
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  settingsItems.unshift({
    key: 'communication-preferences',
    id: 'communication-preferences',
    transparent: true,
    title: t('communicationPreferences.title'),
    BodyPre: t('communicationPreferences.description'),
    testId: 'communication-preferences',
    disabled: false,
    Avatar: (
      <Box paddingL={8} paddingR={4}>
        <Icon name="Envelope" size="large" />
      </Box>
    ),
    onItemClick: e =>
      handleItemClick(e, `${PathNames.SETTINGS}${PathNames.COMMUNICATION_PREFERENCES}`),
    CustomAction: (
      <Button
        onClick={e =>
          handleItemClick(e, `${PathNames.SETTINGS}${PathNames.COMMUNICATION_PREFERENCES}`)
        }
        iconElement={<Icon name="CaretRight" />}
        variant="ghost"
      />
    ),
  })

  return <ActionList id="manage-account" items={settingsItems} />
}
